import { Injectable } from '@angular/core';
import { ChartComponent } from 'ng-apexcharts';
import { GraphDataModel, DataTakerStaffDataModel } from '../../core/api/models';
import html2canvas from 'html2canvas';
import { AlertService } from './alert.service';
import { AnnotationColorLegendItem } from '../../graphs/annotations/annotation-color-legend/annotation-color-legend.model';

export interface PrintHeaderInfo {
  graphTitle: string;
  graphType: string;
  componentType?: string; // 'Program Graph', 'Target Graph', or 'Behavior Graph'
  patientName: string;
  timePeriod: string;
  groupingLabel?: string;
  displayOptions?: string[];
  subtitleItems?: string[];
  selectedTargets?: string[];
  selectedBehaviors?: string[];
  selectedPrograms?: string[];
  selectedProgram?: string; // Single selected program name
  selectedTarget?: string; // Single selected target name
  selectedBehavior?: string; // Single selected behavior name
  staffMember?: string;
  annotations?: string[];
  annotationColorLegends?: AnnotationColorLegendItem[][];
}

export type GraphDownloadFormat = 'svg' | 'png' | 'csv';

@Injectable({
  providedIn: 'root'
})
export class PrintService {

  constructor(private alertService: AlertService) { }

  async downloadApexChart(
    chartContext: any,
    format: GraphDownloadFormat,
    legendItems: AnnotationColorLegendItem[]
  ): Promise<void> {
    if (!chartContext?.exports || !chartContext?.w) {
      return;
    }

    try {
      if (format === 'csv') {
        this.downloadApexChartCsv(chartContext, legendItems);
        return;
      }

      const svgExport = await this.buildApexChartSvg(chartContext, legendItems);
      if (!svgExport) {
        return;
      }

      const filename = this.getApexExportFilename(chartContext, format);
      if (format === 'svg') {
        const svgBlob = new Blob([svgExport.svg], {
          type: 'image/svg+xml;charset=utf-8',
        });
        this.triggerDownload(svgBlob, filename, '.svg');
        return;
      }

      const image = new Image();
      // Match Apex's dataURI rasterization. Blob-backed SVGs containing its HTML
      // legend can taint the canvas and make PNG export throw SecurityError.
      image.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('Unable to render chart image.'));
        image.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgExport.svg);
      });

      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(svgExport.width);
      canvas.height = Math.ceil(svgExport.height);
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Unable to create the chart image.');
      }

      const background = chartContext.w.config.chart.background;
      context.fillStyle = !background || background === 'transparent' ? '#ffffff' : background;
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const pngBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => blob
          ? resolve(blob)
          : reject(new Error('Unable to create the chart image.')), 'image/png');
      });
      this.triggerDownload(pngBlob, filename, '.png');
    } catch (error) {
      console.error('Error exporting chart:', error);
      this.alertService.error('Error exporting chart: ' + error);
    }
  }

  private async buildApexChartSvg(
    chartContext: any,
    legendItems: AnnotationColorLegendItem[]
  ): Promise<{ svg: string; width: number; height: number } | null> {
    chartContext.exports.cleanup?.();
    const chartSvg = await chartContext.exports.getSvgString();
    if (!chartSvg) {
      return null;
    }

    const svgDocument = new DOMParser().parseFromString(chartSvg, 'image/svg+xml');
    if (svgDocument.querySelector('parsererror')) {
      throw new Error('Unable to read the chart SVG.');
    }

    const svg = svgDocument.documentElement;
    const viewBox = (svg.getAttribute('viewBox') ?? '')
      .split(/\s+/)
      .map((value) => Number(value));
    const viewBoxX = Number.isFinite(viewBox[0]) ? viewBox[0] : 0;
    const viewBoxY = Number.isFinite(viewBox[1]) ? viewBox[1] : 0;
    const viewBoxWidth = Number.isFinite(viewBox[2]) && viewBox[2] > 0
      ? viewBox[2]
      : Number(chartContext.w.globals.svgWidth) || 800;
    const chartHeight = Number.isFinite(viewBox[3]) && viewBox[3] > 0
      ? viewBox[3]
      : parseFloat(svg.getAttribute('height') ?? '') ||
        parseFloat(chartContext.w.globals.dom?.elWrap?.style?.height ?? '') ||
        350;
    const renderedWidth = parseFloat(svg.getAttribute('width') ?? '') || viewBoxWidth;
    const renderedChartHeight = parseFloat(svg.getAttribute('height') ?? '') || chartHeight;
    const namespace = 'http://www.w3.org/2000/svg';
    const groupLabels: Record<string, string> = {
      x: 'X Axis',
      y: 'Y Axis',
      other: 'Other',
    };
    const groupOrder = ['x', 'y', 'other'];
    const legendGroup = svgDocument.createElementNS(namespace, 'g');
    legendGroup.setAttribute('class', 'annotation-color-legend-export');
    legendGroup.setAttribute('transform', `translate(0 ${viewBoxY + chartHeight})`);

    const background = svgDocument.createElementNS(namespace, 'rect');
    background.setAttribute('x', String(viewBoxX));
    background.setAttribute('y', '0');
    background.setAttribute('width', String(viewBoxWidth));
    background.setAttribute('fill', '#ffffff');
    legendGroup.appendChild(background);

    const title = svgDocument.createElementNS(namespace, 'text');
    title.setAttribute('x', String(viewBoxX + 20));
    title.setAttribute('y', '30');
    title.setAttribute('fill', '#719eb6');
    title.setAttribute('font-family', 'Arial, sans-serif');
    title.setAttribute('font-size', '16');
    title.setAttribute('font-weight', '700');
    title.textContent = 'Annotation Colors';
    legendGroup.appendChild(title);

    const maxCharacters = Math.max(16, Math.floor((viewBoxWidth - 80) / 7));
    const wrapText = (value: string): string[] => {
      const segments = value
        .split(/\s+/)
        .filter(Boolean)
        .flatMap((word) => {
          const chunks: string[] = [];
          for (let index = 0; index < word.length; index += maxCharacters) {
            chunks.push(word.slice(index, index + maxCharacters));
          }
          return chunks;
        });
      const lines: string[] = [];
      segments.forEach((segment) => {
        const current = lines[lines.length - 1];
        if (!current || current.length + segment.length + 1 > maxCharacters) {
          lines.push(segment);
        } else {
          lines[lines.length - 1] = `${current} ${segment}`;
        }
      });
      return lines.length ? lines : [''];
    };

    let currentY = 55;
    groupOrder.forEach((group) => {
      const items = legendItems.filter((item) => item.group === group);
      if (!items.length) {
        return;
      }

      const groupTitle = svgDocument.createElementNS(namespace, 'text');
      groupTitle.setAttribute('x', String(viewBoxX + 20));
      groupTitle.setAttribute('y', String(currentY));
      groupTitle.setAttribute('fill', '#333333');
      groupTitle.setAttribute('font-family', 'Arial, sans-serif');
      groupTitle.setAttribute('font-size', '14');
      groupTitle.setAttribute('font-weight', '700');
      groupTitle.textContent = groupLabels[group];
      legendGroup.appendChild(groupTitle);
      currentY += 24;

      items.forEach((item) => {
        const displayLabel = item.detail ? `${item.label} - ${item.detail}` : item.label;
        const lines = wrapText(displayLabel);
        const swatch = svgDocument.createElementNS(namespace, 'rect');
        swatch.setAttribute('x', String(viewBoxX + 30));
        swatch.setAttribute('y', String(currentY - 11));
        swatch.setAttribute('width', '12');
        swatch.setAttribute('height', '12');
        swatch.setAttribute('rx', '2');
        swatch.setAttribute('fill', this.normalizeLegendColor(item.color));
        legendGroup.appendChild(swatch);

        const label = svgDocument.createElementNS(namespace, 'text');
        label.setAttribute('x', String(viewBoxX + 50));
        label.setAttribute('y', String(currentY));
        label.setAttribute('fill', '#4a6f82');
        label.setAttribute('font-family', 'Arial, sans-serif');
        label.setAttribute('font-size', '13');
        label.setAttribute('font-weight', '600');
        lines.forEach((line, index) => {
          const textLine = svgDocument.createElementNS(namespace, 'tspan');
          textLine.setAttribute('x', String(viewBoxX + 50));
          if (index > 0) {
            textLine.setAttribute('dy', '18');
          }
          textLine.textContent = line;
          label.appendChild(textLine);
        });
        legendGroup.appendChild(label);
        currentY += lines.length * 18 + 6;
      });
    });

    const legendHeight = currentY + 10;
    background.setAttribute('height', String(legendHeight));
    svg.appendChild(legendGroup);
    svg.setAttribute(
      'viewBox',
      `${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${chartHeight + legendHeight}`
    );
    const heightScale = renderedChartHeight / chartHeight;
    const renderedHeight = renderedChartHeight + legendHeight * heightScale;
    svg.setAttribute('width', String(renderedWidth));
    svg.setAttribute('height', String(renderedHeight));

    return {
      svg: new XMLSerializer().serializeToString(svg).replace(/&nbsp;/g, '&#160;'),
      width: renderedWidth,
      height: renderedHeight,
    };
  }

  private downloadApexChartCsv(
    chartContext: any,
    legendItems: AnnotationColorLegendItem[]
  ): void {
    const exporter = chartContext.exports;
    const originalTriggerDownload = exporter.triggerDownload;
    let chartHref = '';
    exporter.triggerDownload = (href: string) => {
      chartHref = href;
    };

    try {
      const csvOptions = chartContext.w.config.chart.toolbar.export.csv;
      exporter.exportToCSV({
        series: chartContext.w.config.series,
        fileName: csvOptions.filename,
        columnDelimiter: csvOptions.columnDelimiter,
      });
    } finally {
      exporter.triggerDownload = originalTriggerDownload;
    }

    const separatorIndex = chartHref.indexOf(',');
    if (separatorIndex < 0) {
      throw new Error('Unable to create the chart CSV.');
    }

    const csvOptions = chartContext.w.config.chart.toolbar.export.csv;
    const delimiter = csvOptions.columnDelimiter || ',';
    const quote = (value: unknown) => {
      const text = String(value ?? '');
      const spreadsheetSafeText = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
      return `"${spreadsheetSafeText.replace(/"/g, '""')}"`;
    };
    const groupLabels: Record<string, string> = {
      x: 'X Axis',
      y: 'Y Axis',
      other: 'Other',
    };
    const legendRows = [
      '',
      '',
      'Annotation Colors',
      ['Group', 'Color', 'Label', 'Date/Value'].map(quote).join(delimiter),
      ...legendItems.map((item) =>
        [groupLabels[item.group], this.normalizeLegendColor(item.color), item.label, item.detail]
          .map(quote)
          .join(delimiter)
      ),
    ];
    const chartCsv = decodeURIComponent(chartHref.slice(separatorIndex + 1)).replace(/\s+$/, '');
    const csvBlob = new Blob([`${chartCsv}${legendRows.join('\n')}`], {
      type: 'text/csv;charset=utf-8',
    });
    this.triggerDownload(csvBlob, this.getApexExportFilename(chartContext, 'csv'), '.csv');
  }

  private getApexExportFilename(chartContext: any, format: GraphDownloadFormat): string {
    return chartContext.w.config.chart.toolbar.export[format]?.filename ||
      chartContext.w.globals.chartID ||
      'chart';
  }

  private triggerDownload(blob: Blob, filename: string, extension: string): void {
    const href = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = href;
    downloadLink.download = `${filename}${extension}`;
    document.body.appendChild(downloadLink);
    try {
      downloadLink.click();
    } finally {
      document.body.removeChild(downloadLink);
      // Give mobile browsers time to consume the file after the click.
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    }
  }

  private normalizeLegendColor(color: string): string {
    return /^#[0-9a-f]{6}$/i.test(color) ? color : '#008FFB';
  }

  /**
   * Generates a sanitized filename from graph information
   */
  generateFilename(headerInfo: PrintHeaderInfo, extension: string = 'png', shouldPrioritizeGraphTitle: boolean, listAllItems: boolean): string {
    if (listAllItems) {
      let filename = headerInfo.graphTitle;
      if (headerInfo.subtitleItems && headerInfo.subtitleItems.length > 0) {
        filename += ' - ' + headerInfo.subtitleItems.join(', ');
      }
      return filename;
    }
    // Get the primary name (target/behavior/program name)
    // Priority: graphTitle  > selectedTargets/selectedBehaviors/selectedPrograms > subtitleItems
    let primaryName = '';
    if (shouldPrioritizeGraphTitle && headerInfo.graphTitle) {
      // Use graphTitle as primary name (useful for individual chart prints)
      primaryName = headerInfo.graphTitle;
    } else if (headerInfo.selectedTargets && headerInfo.selectedTargets.length > 0) {
      primaryName = headerInfo.selectedTargets[0];
    } else if (headerInfo.selectedBehaviors && headerInfo.selectedBehaviors.length > 0) {
      primaryName = headerInfo.selectedBehaviors[0];
    } else if (headerInfo.selectedPrograms && headerInfo.selectedPrograms.length > 0) {
      primaryName = headerInfo.selectedPrograms[0];
    } else if (headerInfo.subtitleItems && headerInfo.subtitleItems.length > 0) {
      primaryName = headerInfo.subtitleItems[0];
    }

    // Format: GraphType - PrimaryName (sanitized)
    const graphType = headerInfo.graphType.replace(/[^a-zA-Z0-9\s-]/g, '').trim();
    const name = primaryName.replace(/[^a-zA-Z0-9\s-]/g, '').trim();

    // If graphType and primaryName are the same (e.g., for individual chart prints),
    // use just the name to avoid duplication
    let filename = '';

    // Check if graphType already contains the primary name (e.g., "Frequency - All Behaviors" contains "All Behaviors")
    const graphTypeEndsWithName = name && graphType.endsWith(` - ${name}`);

    if (graphType && graphType !== name && !graphTypeEndsWithName) {
      filename = graphType;
      if (name) {
        filename += ` - ${name}`;
      }
    } else if (graphTypeEndsWithName || graphType === name) {
      // If graphType already contains the name, or they're the same, use just graphType
      filename = graphType;
    } else if (name) {
      // Use just the name if graphType is empty or matches the name
      filename = name;
    } else if (graphType) {
      // Fallback to graphType if no name is available
      filename = graphType;
    }

    // Replace spaces with underscores and limit length
    filename = filename.replace(/\s+/g, '_').substring(0, 100);

    // Remove trailing underscores
    filename = filename.replace(/_+$/, '');

    return `${filename}.${extension}`;
  }

  /**
   * Triggers a custom print with professional header
   * @param chartComponent The ApexCharts component
   * @param headerInfo Information to display in the print header
   */
  async printWithHeader(chartComponent: ChartComponent, headerInfo: PrintHeaderInfo): Promise<void> {
    if (!chartComponent || !chartComponent.chart) {
      console.warn('Chart component not available for printing');
      return;
    }

    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      console.error('Unable to open print window');
      return;
    }

    try {
      // Get the chart as data URI (image)
      const chartData = await chartComponent.dataURI();
      const imgURI = typeof chartData === 'string' ? chartData : ('imgURI' in chartData ? chartData.imgURI : null);
      if (!imgURI) {
        console.error('Unable to get chart image data');
        printWindow.close();
        return;
      }

      // Create the print content
      const printContent = this.createPrintContentWithImages(
        [imgURI],
        headerInfo,
        headerInfo.annotationColorLegends
      );

      // Write content to print window
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 100);
      };
    } catch (error) {
      console.error('Error generating chart image:', error);
      printWindow.close();
    }
  }

  /**
   * Returns the HTML string for the print page (used for downloads)
   * Temporarily resizes charts to landscape dimensions for better export quality
   */
  async buildHtml(chartComponent: ChartComponent, headerInfo: PrintHeaderInfo): Promise<string | null> {
    if (!chartComponent || !chartComponent.chart) {
      return null;
    }

    try {
      const apexChart = chartComponent.chart as any;
      const chartDataUri = apexChart.dataURI
        ? await apexChart.dataURI({ format: 'png', scale: 2 })
        : await chartComponent.dataURI();
      const chartData = typeof chartDataUri === 'string' ? { imgURI: chartDataUri } : chartDataUri;
      const imageUri = chartData?.imgURI || '';
      if (!imageUri) return null;
      return this.createPrintContentWithImages(
        [imageUri],
        headerInfo,
        headerInfo.annotationColorLegends
      );
    } catch (error) {
      console.error('Error building HTML for export:', error);
      return null;
    }
  }

  /**
   * Returns the HTML string for multiple charts on the same page
   * Temporarily resizes charts to landscape dimensions for better export quality
   */
  async buildHtmlForCharts(chartComponents: ChartComponent[], headerInfo: PrintHeaderInfo): Promise<string | null> {
    if (!chartComponents || chartComponents.length === 0) {
      return null;
    }

    try {
      const chartExports = await Promise.all(
        chartComponents.map(async (c, index) => {
          if (!c.chart) return { image: '', legend: [] as AnnotationColorLegendItem[] };
          const apexChart = c.chart as any;
          const chartDataUri = apexChart.dataURI
            ? await apexChart.dataURI({ format: 'png', scale: 2 })
            : await c.dataURI();
          return {
            image: typeof chartDataUri === 'string' ? chartDataUri : chartDataUri?.imgURI || '',
            legend: headerInfo.annotationColorLegends?.[index] ?? [],
          };
        })
      );
      const validChartExports = chartExports.filter((chartExport) => !!chartExport.image);
      if (validChartExports.length === 0) return null;
      return this.createPrintContentWithImages(
        validChartExports.map((chartExport) => chartExport.image),
        headerInfo,
        validChartExports.map((chartExport) => chartExport.legend)
      );
    } catch (error) {
      console.error('Error building HTML for multiple charts:', error);
      return null;
    }
  }

  /**
   * Download the composed page as a PNG image using the same HTML layout as PDF
   */
  async downloadPngPage(chartComponent: ChartComponent | undefined, headerInfo: PrintHeaderInfo, isIndividualChart: boolean = false, listAllItems: boolean = false): Promise<void> {
    if (!chartComponent) return;

    try {
      // Create a hidden container with the HTML content
      const html = await this.buildHtml(chartComponent, headerInfo);
      if (!html) return;

      // Create a temporary iframe to properly render the HTML
      // Start with a large width to measure content, will be adjusted later
      const iframe = document.createElement('iframe');
      iframe.style.cssText = `
        position: fixed;
        top: -10000px;
        left: -10000px;
        width: 3000px;
        height: 4000px;
        border: none;
        visibility: hidden;
      `;
      document.body.appendChild(iframe);

      // Write HTML to iframe
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        document.body.removeChild(iframe);
        this.alertService.error('Error creating iframe for PNG generation');
        return;
      }

      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();

      // Wait for iframe content to settle. Use timeout fallback so export cannot hang
      // if the load event is missed or not fired consistently by the browser.
      await new Promise<void>((resolve) => {
        let resolved = false;
        const finalize = () => {
          if (resolved) return;
          resolved = true;
          resolve();
        };

        const timeoutId = setTimeout(finalize, 2000);
        iframe.onload = () => {
          clearTimeout(timeoutId);
          setTimeout(finalize, 700);
        };

        // If content is already complete before onload handler runs.
        if (iframeDoc.readyState === 'complete') {
          clearTimeout(timeoutId);
          setTimeout(finalize, 300);
        }
      });

      // Find the chart image to measure its actual width
      const chartImage = iframeDoc.querySelector('.chart-image') as HTMLImageElement;
      let chartWidth = 0;

      if (chartImage) {
        // Wait for image to load if it hasn't already
        if (!chartImage.complete) {
          await new Promise(resolve => {
            chartImage.onload = resolve;
            chartImage.onerror = resolve; // Continue even if image fails
            setTimeout(resolve, 3000); // Timeout after 3 seconds (increased from 2 seconds)
          });
        }
        chartWidth = chartImage.naturalWidth || chartImage.offsetWidth || chartImage.width;
      }

      // If we couldn't get chart width, fall back to measuring the chart container
      if (!chartWidth || chartWidth === 0) {
        const chartContainer = iframeDoc.querySelector('.chart-container');
        if (chartContainer) {
          chartWidth = chartContainer.scrollWidth || (chartContainer as HTMLElement).offsetWidth;
        }
      }

      // Use chart width plus small margin (header will wrap to fit)
      const margin = 40; // Small margin on each side
      const canvasWidth = chartWidth > 0 ? chartWidth + (margin * 2) : iframeDoc.body.scrollWidth;
      // Update iframe width to match calculated width
      iframe.style.width = `${canvasWidth}px`;

      // Wait longer for iframe to resize and all elements to settle (increased from 100ms)
      await new Promise(resolve => setTimeout(resolve, 500));
      const contentHeight = iframeDoc.body.scrollHeight;

      // Capture the iframe content
      const canvas = await html2canvas(iframeDoc.body, {
        width: canvasWidth,
        height: contentHeight,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false
      });

      // Create download link
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = this.generateFilename(headerInfo, 'png', isIndividualChart, listAllItems);
      a.click();

      // Clean up
      document.body.removeChild(iframe);

    } catch (error) {
      console.error('Error generating PNG:', error);
      this.alertService.error('Error generating PNG: ' + error);
    }
  }

  /**
   * Download PNG for multiple charts (synced graphs)
   */
  async downloadPngPageForCharts(chartComponents: ChartComponent[] | undefined, headerInfo: PrintHeaderInfo, isIndividualChart: boolean = false, listAllItems: boolean = false): Promise<void> {
    if (!chartComponents || chartComponents.length === 0) return;

    try {
      const html = await this.buildHtmlForCharts(chartComponents, headerInfo);
      if (!html) return;

      // Start with a large width to measure content, will be adjusted later
      const iframe = document.createElement('iframe');
      iframe.style.cssText = `
        position: fixed;
        top: -10000px;
        left: -10000px;
        width: 3000px;
        height: 4000px;
        border: none;
        visibility: hidden;
      `;
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        document.body.removeChild(iframe);
        this.alertService.error('Error creating iframe for PNG generation');
        return;
      }

      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();

      await new Promise<void>((resolve) => {
        let resolved = false;
        const finalize = () => {
          if (resolved) return;
          resolved = true;
          resolve();
        };

        const timeoutId = setTimeout(finalize, 2000);
        iframe.onload = () => {
          clearTimeout(timeoutId);
          setTimeout(finalize, 700);
        };

        if (iframeDoc.readyState === 'complete') {
          clearTimeout(timeoutId);
          setTimeout(finalize, 300);
        }
      });

      // Find the widest chart image to measure its actual width
      const chartImages = iframeDoc.querySelectorAll('.chart-image') as NodeListOf<HTMLImageElement>;
      let maxChartWidth = 0;

      if (chartImages.length > 0) {
        // Wait for all images to load
        await Promise.all(Array.from(chartImages).map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise(resolve => {
            img.onload = resolve;
            img.onerror = resolve;
            setTimeout(resolve, 2000); // Timeout after 2 seconds
          });
        }));

        // Find the widest chart
        chartImages.forEach(img => {
          const width = img.naturalWidth || img.offsetWidth || img.width;
          if (width > maxChartWidth) {
            maxChartWidth = width;
          }
        });
      }

      // If we couldn't get chart width, fall back to measuring the chart containers
      if (!maxChartWidth || maxChartWidth === 0) {
        const chartContainers = iframeDoc.querySelectorAll('.chart-container');
        chartContainers.forEach(container => {
          const width = container.scrollWidth || (container as HTMLElement).offsetWidth;
          if (width > maxChartWidth) {
            maxChartWidth = width;
          }
        });
      }

      // Use chart width plus small margin (header will wrap to fit)
      const margin = 40; // Small margin on each side
      const canvasWidth = maxChartWidth > 0 ? maxChartWidth + (margin * 2) : iframeDoc.body.scrollWidth;
      // Update iframe width to match calculated width
      iframe.style.width = `${canvasWidth}px`;

      // Wait a bit for iframe to resize
      await new Promise(resolve => setTimeout(resolve, 100));
      const contentHeight = iframeDoc.body.scrollHeight;

      const canvas = await html2canvas(iframeDoc.body, {
        width: canvasWidth,
        height: contentHeight,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false
      });

      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = this.generateFilename(headerInfo, 'png', isIndividualChart, listAllItems);
      a.click();

      document.body.removeChild(iframe);

    } catch (error) {
      console.error('Error generating PNG:', error);
      this.alertService.error('Error generating PNG: ' + error);
    }
  }

  /**
   * Download the composed page as PDF by opening the HTML in a new window and triggering print-to-PDF.
   */
  async downloadPdf(chartComponent: ChartComponent | undefined, headerInfo: PrintHeaderInfo): Promise<void> {
    if (!chartComponent) return;
    const chartData = await this.buildHtml(chartComponent, headerInfo);
    if (!chartData) return;

    // Extract the chart image URI from the HTML
    const chartImageMatch = chartData.match(/<img src="([^"]+)" alt="Chart"/);
    if (!chartImageMatch || !chartImageMatch[1]) return;

    // Create PDF-specific HTML with header on each page
    const html = this.createPrintContentWithImagesForPdf(
      [chartImageMatch[1]],
      headerInfo,
      headerInfo.annotationColorLegends
    );
    // Create hidden iframe to avoid opening a separate window and suppress extra tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      }, 100);
    };
  }

  /**
   * Download PDF for multiple charts (synced graphs)
   */
  async downloadPdfForCharts(chartComponents: ChartComponent[] | undefined, headerInfo: PrintHeaderInfo): Promise<void> {
    if (!chartComponents || chartComponents.length === 0) return;
    const chartData = await this.buildHtmlForCharts(chartComponents, headerInfo);
    if (!chartData) return;

    // Extract all chart image URIs from the HTML
    const chartImageMatches = chartData.matchAll(/<img src="([^"]+)" alt="Chart"/g);
    const chartImageURIs: string[] = [];
    for (const match of chartImageMatches) {
      if (match[1]) {
        chartImageURIs.push(match[1]);
      }
    }
    if (chartImageURIs.length === 0) return;

    // Create PDF-specific HTML with header on each page
    const html = this.createPrintContentWithImagesForPdf(
      chartImageURIs,
      headerInfo,
      headerInfo.annotationColorLegends
    );
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      }, 100);
    };
  }

  /**
   * Creates the header HTML section
   */
  private createHeaderSection(headerInfo: PrintHeaderInfo): string {
    // If graphType already includes a behavior/target/program name (indicated by " - "), 
    // don't add " Graph" suffix to avoid redundancy
    const graphTypeDisplay = headerInfo.graphType.includes(' - ')
      ? headerInfo.graphType
      : `${headerInfo.graphType} Graph${headerInfo.componentType ? ` - ${headerInfo.componentType}` : ''}`;

    return `
      <div class="print-header">
        <div class="header-main">
          <h1 class="graph-title">${graphTypeDisplay}</h1>
          ${this.createSubtitle(headerInfo)}
        </div>
        
        <div class="header-info">
          <div class="info-section">
            <h3>Graph Information</h3>
            <div class="info-line">
              <span><strong>Client:</strong> ${headerInfo.patientName}</span>
              <span><strong>Time Period:</strong> ${headerInfo.timePeriod}</span>
              <span><strong>Grouped By:</strong> ${headerInfo.groupingLabel || 'N/A'}</span>
              ${this.createDisplayOptionsInline(headerInfo)}
              ${this.createStaffMemberInline(headerInfo)}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Creates the complete print content with header and chart image (for PNG - header only at top)
   */
  private createPrintContentWithImages(
    chartImageURIs: string[],
    headerInfo: PrintHeaderInfo,
    annotationColorLegends: AnnotationColorLegendItem[][] = []
  ): string {
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Graph Report - ${headerInfo.graphType} Graph</title>
        <style>
          ${this.getPrintStyles()}
        </style>
      </head>
      <body>
        <div class="print-container">
          ${this.createHeaderSection(headerInfo)}
          
          <!-- Charts Container -->
          <div class="charts-wrapper">
            ${chartImageURIs.map((uri, index) => `
              <div class="chart-container">
                <img src="${uri}" alt="Chart" class="chart-image" />
                ${this.createAnnotationColorLegendSection(annotationColorLegends[index] ?? [])}
              </div>
            `).join('')}
          </div>
          
          <!-- Print Footer -->
          <div class="print-footer">
            <p>Generated by OptoMiser SPARK - ${currentDate}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Creates the complete print content for PDF with header on each page
   */
  private createPrintContentWithImagesForPdf(
    chartImageURIs: string[],
    headerInfo: PrintHeaderInfo,
    annotationColorLegends: AnnotationColorLegendItem[][] = []
  ): string {
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Graph Report - ${headerInfo.graphType} Graph</title>
        <style>
          ${this.getPrintStyles()}
        </style>
      </head>
      <body>
        <div class="print-container">
          <!-- Charts Container with header on each page -->
          <div class="charts-wrapper">
            ${chartImageURIs.map((uri, index) => `
              <div class="chart-page">
                ${this.createHeaderSection(headerInfo)}
                <div class="chart-container">
                  <img src="${uri}" alt="Chart" class="chart-image" />
                  ${this.createAnnotationColorLegendSection(annotationColorLegends[index] ?? [])}
                </div>
                <div class="print-footer">
                  <p>Generated by OptoMiser SPARK - ${currentDate}</p>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      </body>
      </html>
    `;
  }

  private createAnnotationColorLegendSection(items: AnnotationColorLegendItem[]): string {
    if (!items.length) {
      return '';
    }

    const groupLabels: Record<string, string> = {
      x: 'X Axis',
      y: 'Y Axis',
      other: 'Other',
    };
    const groups = ['x', 'y', 'other']
      .map((group) => ({
        group,
        title: groupLabels[group],
        items: items.filter((item) => item.group === group),
      }))
      .filter((group) => group.items.length > 0);

    return `
      <div class="annotation-color-legend" data-testid="annotation-color-legend-export">
        <div class="annotation-color-legend-title">Annotation Colors</div>
        ${groups.map((group) => `
          <div class="annotation-color-legend-group">
            <div class="annotation-color-legend-group-title">${group.title}</div>
            <div class="annotation-color-legend-items">
              ${group.items.map((item) => {
                const displayLabel = item.detail ? `${item.label} - ${item.detail}` : item.label;
                return `
                  <div class="annotation-color-legend-item">
                    <span class="annotation-color-legend-swatch" style="background-color: ${this.normalizeLegendColor(item.color)}"></span>
                    <span>${this.encodeHtml(displayLabel)}</span>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  private encodeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Creates the annotations section
   */
  private createAnnotationsSection(headerInfo: PrintHeaderInfo): string {
    if (!headerInfo.annotations || headerInfo.annotations.length === 0) {
      return '';
    }

    return `
      <div class="info-section">
        <h3>Annotations</h3>
        <ul>
          ${headerInfo.annotations.map(annotation =>
      `<li> ${annotation} </li>`
    ).join('')}
        </ul>
      </div>
    `;
  }

  /**
   * Creates the targets/behaviors/programs section
   */
  private createTargetsSection(headerInfo: PrintHeaderInfo): string {
    let sections = [];

    if (headerInfo.selectedTargets && headerInfo.selectedTargets.length > 0) {
      sections.push(`
        <p><strong>Selected Targets:</strong></p>
        <ul>
          ${headerInfo.selectedTargets.map(target => `<li>${target}</li>`).join('')}
        </ul>
      `);
    }

    if (headerInfo.selectedBehaviors && headerInfo.selectedBehaviors.length > 0) {
      sections.push(`
        <p><strong>Selected Behaviors:</strong></p>
        <ul>
          ${headerInfo.selectedBehaviors.map(behavior => `<li>${behavior}</li>`).join('')}
        </ul>
      `);
    }

    if (headerInfo.selectedPrograms && headerInfo.selectedPrograms.length > 0) {
      sections.push(`
        <p><strong>Selected Programs:</strong></p>
        <ul>
          ${headerInfo.selectedPrograms.map(program => `<li>${program}</li>`).join('')}
        </ul>
      `);
    }

    return sections.join('');
  }

  /**
   * Creates the display options inline snippet
   */
  private createDisplayOptionsSection(headerInfo: PrintHeaderInfo): string {
    if (!headerInfo.displayOptions || headerInfo.displayOptions.length === 0) {
      return '';
    }
    return `
      <p><strong>Display Options:</strong> ${headerInfo.displayOptions.join(', ')}</p>
    `;
  }

  /**
   * Creates the display options as an inline span
   */
  private createDisplayOptionsInline(headerInfo: PrintHeaderInfo): string {
    if (!headerInfo.displayOptions || headerInfo.displayOptions.length === 0) {
      return '';
    }
    return `<span><strong>Display Options:</strong> ${headerInfo.displayOptions.join(', ')}</span>`;
  }

  /**
   * Creates the staff members section
   */
  private createStaffMemberSection(headerInfo: PrintHeaderInfo): string {
    if (!headerInfo.staffMember) {
      return '';
    }
    return `
      <p><strong>Staff Member:</strong> ${headerInfo.staffMember}</p>
    `;
  }

  /**
   * Creates the staff member as an inline span
   */
  private createStaffMemberInline(headerInfo: PrintHeaderInfo): string {
    if (!headerInfo.staffMember) {
      return '';
    }
    return `<span><strong>Staff Member:</strong> ${headerInfo.staffMember}</span>`;
  }

  /**
   * Creates subtitle listing selected items with names and types
   */
  private createSubtitle(headerInfo: PrintHeaderInfo): string {
    const items: string[] = [];

    // Add selected program/target/behavior if available
    if (headerInfo.selectedProgram) {
      items.push(`Selected Program: `);
    }
    if (headerInfo.selectedTarget) {
      items.push(`Selected Target: `);
    }
    if (headerInfo.selectedBehavior) {
      items.push(`Selected Behavior: `);
    }

    // Add other subtitle items
    if (headerInfo.subtitleItems && headerInfo.subtitleItems.length > 0) {
      items.push(...headerInfo.subtitleItems);
    }

    if (items.length === 0) return '';
    return `
      <div class="graph-subtitle">${items.join(' • ')}</div>
    `;
  }

  /**
   * Gets the print-specific CSS styles
   */
  private getPrintStyles(): string {
    return `
      @page {
        margin: 0.5in;
        size: A4 landscape;
      }
      
      body {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        margin: 0;
        padding: 0;
        color: #333;
        line-height: 1.4;
      }
      
      .print-container {
        max-width: 100%;
        margin: 0 auto;
      }
      
      .print-header {
        border-bottom: 3px solid #719eb6;
        padding-bottom: 10px;
        margin-bottom: 15px;
        page-break-inside: avoid;
        flex-shrink: 0;
      }
      
      .header-main {
        text-align: center;
        margin-bottom: 15px;
      }
      
      .graph-title {
        font-size: 26px;
        font-weight: bold;
        color: #719eb6;
        margin: 0 0 8px 0;
        text-transform: uppercase;
        letter-spacing: 1px;
        line-height: 1.2;
      }

      .graph-subtitle {
        font-size: 13px;
        color: #666;
        margin-top: -3px;
        margin-bottom: 8px;
        line-height: 1.3;
      }
      
      .header-info {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 25px;
        margin-top: 12px;
        align-items: flex-start;
      }
      
      .info-section {
        background: #f8f9fa;
        padding: 12px;
        border-radius: 8px;
        border-left: 4px solid #719eb6;
        flex: 1;
        min-width: 200px;
      }
      
      .info-section h3 {
        font-size: 15px;
        font-weight: bold;
        color: #719eb6;
        margin-top: 0;
        margin-bottom: 8px;
      }
      
      .info-line {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 18px;
        align-items: center;
        font-size: 13px;
      }
      
      .info-line span {
        white-space: nowrap;
      }
      
      .info-section p {
        margin: 5px 0;
        font-size: 14px;
      }
      
      .info-section ul {
        margin: 10px 0;
        padding-left: 20px;
      }
      
      .info-section li {
        margin: 5px 0;
        font-size: 14px;
      }
      
      .charts-wrapper {
        display: flex;
        flex-direction: column;
        gap: 40px;
        margin: 40px 0;
      }
      
      .chart-page {
        page-break-after: always;
        break-after: page;
        display: flex;
        flex-direction: column;
        min-height: calc(100vh - 1in);
      }
      
      .chart-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }
      
      /* Chart container for PNG layout (direct child of charts-wrapper) */
      .charts-wrapper > .chart-container {
        text-align: center;
        page-break-inside: avoid;
        width: 100%;
        display: block;
        margin: 20px 0;
      }
      
      /* Chart container for PDF layout (inside chart-page) */
      .chart-page .chart-container {
        text-align: center;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        flex: 1 1 auto;
        min-height: 0;
      }
      
      /* Ensure one chart per page (for PNG layout) */
      .charts-wrapper > .chart-container:not(:last-child) {
        page-break-after: always;
        break-after: page;
      }
      
      .chart-image {
        max-width: 100%;
        height: auto;
        width: auto;
        object-fit: contain;
        display: block;
        margin: 0 auto;
      }

      .chart-page .chart-image {
        min-height: 0;
      }

      .annotation-color-legend {
        box-sizing: border-box;
        margin: 18px auto 0;
        padding: 0 20px;
        width: 100%;
        text-align: left;
        color: #4a6f82;
        flex-shrink: 0;
      }

      .annotation-color-legend-title {
        margin-bottom: 8px;
        color: #719eb6;
        font-size: 16px;
        font-weight: 700;
      }

      .annotation-color-legend-group {
        margin-bottom: 8px;
      }

      .annotation-color-legend-group-title {
        margin-bottom: 5px;
        color: #333;
        font-size: 14px;
        font-weight: 700;
      }

      .annotation-color-legend-items {
        display: flex;
        flex-wrap: wrap;
        column-gap: 24px;
        row-gap: 7px;
      }

      .annotation-color-legend-item {
        display: flex;
        align-items: flex-start;
        min-width: 220px;
        max-width: 100%;
        font-size: 13px;
        font-weight: 600;
        overflow-wrap: anywhere;
      }

      .annotation-color-legend-swatch {
        display: inline-block;
        width: 12px;
        height: 12px;
        margin: 3px 8px 0 0;
        border-radius: 2px;
        flex: 0 0 12px;
      }
      
      .print-footer {
        border-top: 2px solid #719eb6;
        padding-top: 8px;
        padding-bottom: 8px;
        margin-top: auto;
        text-align: center;
        font-size: 11px;
        color: #666;
        page-break-inside: avoid;
        flex-shrink: 0;
        page-break-after: avoid;
      }
      
      @media print {
        .print-header {
          border-bottom: 3px solid #719eb6;
        }
        
        .graph-title {
          color: #719eb6;
        }
        
        .info-section {
          border-left: 4px solid #719eb6;
        }
        
        .info-section h3 {
          color: #719eb6;
        }
        
        .print-footer {
          border-top: 2px solid #719eb6;
        }
      }
    `;
  }

}
